import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Target,
  ArrowLeft,
  Save,
  Send,
  Check,
  X,
  Lightbulb,
  BarChart3,
  Clock,
  Star,
  TrendingUp
} from 'lucide-react';

export default function ObjectiveFormPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = id && id !== 'new';
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  
  const [objective, setObjective] = useState({
    title: '',
    description: '',
    specific: '',
    measurable: '',
    achievable: '',
    relevant: '',
    timeBound: '',
    weight: 25,
    category: 'performance',
    deadline: '',
    campaignId: '',
    employeeId: user?.id || ''
  });

  useEffect(() => {
    fetchCampaigns();
    if (isEditing) {
      fetchObjective();
    }
  }, [id]);

  const fetchCampaigns = async () => {
    try {
      const response = await api.get(`/campaigns?company_id=${user?.companyId}&status=active`);
      setCampaigns(response.data.campaigns || []);
      
      // Auto-select if only one campaign
      if (response.data.campaigns?.length === 1 && !isEditing) {
        setObjective(prev => ({...prev, campaignId: response.data.campaigns[0].id}));
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const fetchObjective = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/objectives/${id}?company_id=${user?.companyId}`);
      const data = response.data;
      setObjective({
        title: data.title || '',
        description: data.description || '',
        specific: data.smart?.specific || '',
        measurable: data.smart?.measurable || '',
        achievable: data.smart?.achievable || '',
        relevant: data.smart?.relevant || '',
        timeBound: data.smart?.timeBound || '',
        weight: data.weight || 25,
        category: data.category || 'performance',
        deadline: data.deadline?.split('T')[0] || '',
        campaignId: data.campaignId || '',
        employeeId: data.employeeId || user?.id,
        progress: data.progress || 0,
        status: data.status || 'draft'
      });
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'objectif');
      navigate('/performance');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (submit = false) => {
    if (!objective.title || !objective.campaignId || !objective.deadline) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    if (!objective.specific || !objective.measurable || !objective.achievable || !objective.relevant || !objective.timeBound) {
      toast.error('Veuillez compléter tous les critères SMART');
      return;
    }
    
    setSaving(true);
    try {
      if (isEditing) {
        await api.put(`/objectives/${id}?company_id=${user?.companyId}&updated_by=${user?.id}`, {
          title: objective.title,
          description: objective.description,
          progress: objective.progress
        });
        toast.success('Objectif mis à jour');
      } else {
        await api.post(`/objectives?company_id=${user?.companyId}&created_by=${user?.id}`, objective);
        toast.success('Objectif créé');
      }
      
      if (submit && !isEditing) {
        // Submit for validation
        const response = await api.get(`/objectives?company_id=${user?.companyId}&employee_id=${user?.id}`);
        const newObjective = response.data.objectives?.[0];
        if (newObjective) {
          await api.post(`/objectives/${newObjective.id}/submit?company_id=${user?.companyId}&submitted_by=${user?.id}`);
          toast.success('Objectif soumis pour validation');
        }
      }
      
      navigate('/performance');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const SmartField = ({ letter, label, description, value, onChange, placeholder, example }) => (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Badge className="bg-webtech-blue text-white w-8 h-8 flex items-center justify-center text-lg font-bold">
          {letter}
        </Badge>
        <Label className="text-base font-semibold">{label}</Label>
      </div>
      <p className="text-sm text-slate-500 ml-10">{description}</p>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="ml-10"
        rows={2}
      />
      {example && (
        <div className="ml-10 flex items-start gap-2 bg-slate-50 p-3 rounded-lg">
          <Lightbulb className="w-4 h-4 text-webtech-yellow mt-0.5 flex-shrink-0" />
          <p className="text-xs text-slate-600"><span className="font-medium">Exemple:</span> {example}</p>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout title="Objectif SMART">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-webtech-blue border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={isEditing ? "Modifier l'objectif" : "Nouvel objectif SMART"}>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/performance')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </div>

        {/* Main Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-webtech-blue" />
              Informations générales
            </CardTitle>
            <CardDescription>
              Définissez les informations de base de votre objectif
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titre de l'objectif *</Label>
                <Input
                  id="title"
                  value={objective.title}
                  onChange={(e) => setObjective({...objective, title: e.target.value})}
                  placeholder="Ex: Améliorer le taux de satisfaction client"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="campaign">Campagne *</Label>
                <Select
                  value={objective.campaignId}
                  onValueChange={(value) => setObjective({...objective, campaignId: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une campagne" />
                  </SelectTrigger>
                  <SelectContent>
                    {campaigns.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={objective.description}
                onChange={(e) => setObjective({...objective, description: e.target.value})}
                placeholder="Décrivez brièvement cet objectif..."
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Catégorie</Label>
                <Select
                  value={objective.category}
                  onValueChange={(value) => setObjective({...objective, category: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="performance">Performance</SelectItem>
                    <SelectItem value="development">Développement</SelectItem>
                    <SelectItem value="team">Équipe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="deadline">Date limite *</Label>
                <Input
                  id="deadline"
                  type="date"
                  value={objective.deadline}
                  onChange={(e) => setObjective({...objective, deadline: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Poids: {objective.weight}%</Label>
                <Slider
                  value={[objective.weight]}
                  onValueChange={(value) => setObjective({...objective, weight: value[0]})}
                  max={100}
                  step={5}
                  className="mt-3"
                />
              </div>
            </div>
            
            {isEditing && objective.status !== 'draft' && (
              <div className="space-y-2">
                <Label>Progression: {objective.progress || 0}%</Label>
                <Slider
                  value={[objective.progress || 0]}
                  onValueChange={(value) => setObjective({...objective, progress: value[0]})}
                  max={100}
                  step={5}
                  className="mt-3"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* SMART Criteria */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-webtech-yellow" />
              Critères SMART
            </CardTitle>
            <CardDescription>
              Un bon objectif doit être Spécifique, Mesurable, Atteignable, Réaliste et Temporel
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <SmartField
              letter="S"
              label="Spécifique"
              description="Qu'est-ce qui doit être accompli exactement ?"
              value={objective.specific}
              onChange={(value) => setObjective({...objective, specific: value})}
              placeholder="Décrivez précisément ce que vous voulez accomplir..."
              example="Augmenter le taux de satisfaction client de notre département"
            />
            
            <SmartField
              letter="M"
              label="Mesurable"
              description="Comment allez-vous mesurer le progrès et le succès ?"
              value={objective.measurable}
              onChange={(value) => setObjective({...objective, measurable: value})}
              placeholder="Quels indicateurs utiliserez-vous ?"
              example="Le taux de satisfaction passera de 75% à 90% selon les enquêtes mensuelles"
            />
            
            <SmartField
              letter="A"
              label="Atteignable"
              description="Est-ce réaliste ? Quelles ressources sont nécessaires ?"
              value={objective.achievable}
              onChange={(value) => setObjective({...objective, achievable: value})}
              placeholder="Expliquez comment vous comptez y arriver..."
              example="Avec la nouvelle formation de l'équipe et les nouveaux outils de support"
            />
            
            <SmartField
              letter="R"
              label="Pertinent (Relevant)"
              description="Pourquoi cet objectif est-il important ? Comment s'aligne-t-il avec les objectifs de l'entreprise ?"
              value={objective.relevant}
              onChange={(value) => setObjective({...objective, relevant: value})}
              placeholder="Expliquez la pertinence de cet objectif..."
              example="La satisfaction client est un KPI stratégique pour la fidélisation"
            />
            
            <SmartField
              letter="T"
              label="Temporel (Time-bound)"
              description="Quelle est la deadline ? Quelles sont les étapes intermédiaires ?"
              value={objective.timeBound}
              onChange={(value) => setObjective({...objective, timeBound: value})}
              placeholder="Définissez les jalons et la date finale..."
              example="Objectif à atteindre d'ici le 31 décembre 2025, avec un point à mi-parcours en juin"
            />
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => navigate('/performance')}>
            Annuler
          </Button>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={() => handleSave(false)}
              disabled={saving}
            >
              <Save className="w-4 h-4 mr-2" />
              Enregistrer brouillon
            </Button>
            <Button 
              onClick={() => handleSave(true)}
              disabled={saving}
              className="bg-webtech-blue hover:bg-webtech-blue-hover"
            >
              <Send className="w-4 h-4 mr-2" />
              {saving ? 'Enregistrement...' : 'Soumettre pour validation'}
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
